import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiResponse } from '@core/models/general.model';
import { environment } from '@environments/environment';
import {
  IDespachoProveedor,
  IEstadoDespacho,
  IDespacho,
  ICreateDespacho,
  IEstadoImportacion,
  IReporteFotograficoItem,
  IDocumentoDespacho,
  ITimelineFechas,
  IInformacionGeneralDespacho,
} from '../models/proveedores.model';

@Injectable({ providedIn: 'root' })
export class DespachosService {
  private readonly http = inject(HttpClient);
  private readonly api = environment.apiUrl;

  getDespachos(
    params: Record<string, string>,
    proveedorId: number,
  ): Observable<ApiResponse<IDespachoProveedor[]>> {
    const qs = new URLSearchParams(params).toString();
    return this.http.get<ApiResponse<IDespachoProveedor[]>>(
      `${this.api}/v1/proveedores/despachos/${proveedorId}?${qs}`,
    );
  }

  searchDespachoByNro(
    empresaId: number,
    nroDespacho: string,
  ): Observable<ApiResponse<{ servicio: number; nroDespacho: string; despachoID: number } | null>> {
    return this.http.get<ApiResponse<{ servicio: number; nroDespacho: string; despachoID: number } | null>>(
      `${this.api}/v1/despachos/tipo-servicio?despacho=${encodeURIComponent(nroDespacho)}&empresaId=${empresaId}`,
    );
  }

  getEstadosDespacho(): Observable<ApiResponse<IEstadoDespacho[]>> {
    return this.http.get<ApiResponse<IEstadoDespacho[]>>(
      `${this.api}/v1/estados-despacho`,
    );
  }

  exportDespachosExcel(
    params: Record<string, string>,
    proveedorId: number,
  ): Observable<Blob> {
    const qs = new URLSearchParams(params).toString();
    return this.http.get(
      `${this.api}/v1/proveedores/despachos/${proveedorId}/export/excel?${qs}`,
      { responseType: 'blob' },
    );
  }

  getAllDespachos(): Observable<ApiResponse<IDespacho[]>> {
    return this.http.get<ApiResponse<IDespacho[]>>(`${this.api}/v1/despachos`);
  }

  getDespachoById(id: number): Observable<ApiResponse<IDespacho>> {
    return this.http.get<ApiResponse<IDespacho>>(
      `${this.api}/v1/despachos/${id}`,
    );
  }

  getEstadosImportacion(
    despachoId: number,
  ): Observable<ApiResponse<IEstadoImportacion[]>> {
    return this.http.get<ApiResponse<IEstadoImportacion[]>>(
      `${this.api}/v1/generardespacho/${despachoId}/estados`,
    );
  }

  getReporteFotografico(
    despachoId: number,
    terceroId: number,
  ): Observable<ApiResponse<IReporteFotograficoItem[]>> {
    return this.http.get<ApiResponse<IReporteFotograficoItem[]>>(
      `${this.api}/v1/herramientas/despachos/${despachoId}/reporte-fotografico`,
      { params: { terceroId: String(terceroId) } },
    );
  }

  downloadFotoDespacho(documentoId: number): Observable<Blob> {
    return this.http.get(`${this.api}/v1/documento/${documentoId}/download`, {
      responseType: 'blob',
    });
  }

  createDespacho(body: ICreateDespacho): Observable<ApiResponse<number>> {
    return this.http.post<ApiResponse<number>>(
      `${this.api}/v1/despachos`,
      body,
    );
  }

  updateDespacho(
    id: number,
    body: ICreateDespacho & { despachoID: number },
  ): Observable<ApiResponse<boolean>> {
    return this.http.put<ApiResponse<boolean>>(
      `${this.api}/v1/despachos/${id}`,
      body,
    );
  }

  getInformacionGeneral(
    despachoId: number,
  ): Observable<ApiResponse<IInformacionGeneralDespacho>> {
    return this.http.get<ApiResponse<IInformacionGeneralDespacho>>(
      `${this.api}/v1/despachos/${despachoId}/resumen`,
    );
  }

  getTimeline(despachoId: number): Observable<ApiResponse<ITimelineFechas>> {
    return this.http.get<ApiResponse<ITimelineFechas>>(
      `${this.api}/v1/despachos/${despachoId}/timeline`,
    );
  }

  guardarRecepcionDocumentos(
    despachoId: number,
    fecha: Date,
    userName: string,
  ): Observable<ApiResponse<boolean>> {
    const body = {
      documentosOriginales: true,
      fechaRecepcion: fecha.toISOString(),
      userName,
    };
    return this.http.post<ApiResponse<boolean>>(
      `${this.api}/v1/generardespacho/${despachoId}/recepcion-documentos`,
      body,
    );
  }

  deleteDespacho(id: number): Observable<ApiResponse<boolean>> {
    return this.http.delete<ApiResponse<boolean>>(
      `${this.api}/v1/despachos/${id}`,
    );
  }

  getDocumentosDespacho(
    despachoId: number,
    terceroId: number,
  ): Observable<ApiResponse<IDocumentoDespacho[]>> {
    return this.http.get<ApiResponse<IDocumentoDespacho[]>>(
      `${this.api}/v1/herramientas/despachos/${despachoId}/documentos`,
      { params: { terceroId: String(terceroId) } },
    );
  }

  descargarTodosDocumentosDespacho(
    despachoId: number,
    terceroId: number,
  ): Observable<Blob> {
    return this.http.get(
      `${this.api}/v1/herramientas/despachos/${despachoId}/documentos/descargar-todos`,
      { params: { terceroId: String(terceroId) }, responseType: 'blob' },
    );
  }

  descargarDocumento(documentoId: number): Observable<Blob> {
    return this.http.get(`${this.api}/v1/documento/${documentoId}/download`, {
      responseType: 'blob',
    });
  }

  getReporteFacturas(despachoId: number): Observable<Blob> {
    return this.http.get(
      `${this.api}/v1/generardespacho/${despachoId}/reporte-facturas`,
      { responseType: 'blob' },
    );
  }

  getIdentificadoresExcel(despachoId: number): Observable<Blob> {
    return this.http.get(
      `${this.api}/v1/generardespacho/${despachoId}/identificadores-excel`,
      { responseType: 'blob' },
    );
  }
}
